import { useState, type ReactNode } from 'react';
import { ArrowLeftIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { formatAbsolute, formatRelative, isDocEmpty } from '@odo/shared-ui';
import {
    DX_REPORT_SECTION_IDS,
    type DxReportPayload,
    type DxReportSection,
    type DxReportSectionId
} from '../../../patient-files/actions/dx-report/types';
import type { TimelineEvent } from '../../../patient-files/actions/types';
import { SECTION_META } from '../../../patient-files/actions/dx-report/editor/editorState';
import { DxReportPdfPane } from '../../../patient-files/actions/dx-report/editor/DxReportPdfPane';
import { useDxReportPdf } from '../../../patient-files/actions/dx-report/editor/useDxReportPdf';
import { formatMoneyCents } from '../../../../utils/money';
import { ReadOnlyDoc } from '../submit-design/DxDraftPreview';
import { useResolvedDxPayload } from '../submit-design/useResolvedDxPayload';
import {
    CARD_TEXT_CLASS,
    HEADER_BUTTON_CLASS,
    PANEL_BODY_CLASS,
    PanelHeader,
    VersionCard
} from '../design-dx/VersionCard';

const MUTED_TEXT_CLASS = 'text-xs text-gray-400 italic';
const LINK_BUTTON_CLASS =
    'flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 transition-colors';
// The editor root brings its own padding and colour; flatten it to the submission text style.
const DOC_TEXT_CLASS = `${CARD_TEXT_CLASS} [&_.ProseMirror]:p-0 [&_.ProseMirror]:text-gray-700`;

const SUMMARY_SECTION_IDS: DxReportSectionId[] = [
    'estimated-treatment-time',
    'orthopulse-recommendation'
];
const DETAIL_SECTION_IDS: DxReportSectionId[] = [
    'treatment-goals',
    'findings',
    'comments',
    'risks',
    'special-consent'
];

function SectionBox({ section }: { section: DxReportSection }) {
    return (
        <VersionCard label={section.title || SECTION_META[section.id].defaultTitle}>
            <div className={DOC_TEXT_CLASS}>
                <ReadOnlyDoc doc={section.tiptapJson} chrome="bare" />
            </div>
        </VersionCard>
    );
}

function DxSections({ payload }: { payload: DxReportPayload }) {
    const filled = new Map(
        payload.sections.filter((s) => !isDocEmpty(s.tiptapJson)).map((s) => [s.id, s])
    );
    const boxes = (ids: DxReportSectionId[]) =>
        ids.flatMap((id) => {
            const section = filled.get(id);
            return section ? [<SectionBox key={id} section={section} />] : [];
        });
    const emptyLabels = DX_REPORT_SECTION_IDS.filter((id) => !filled.has(id)).map(
        (id) => SECTION_META[id].navLabel
    );

    return (
        <div className="flex flex-col gap-4">
            {/* Wraps on the panel's width, not the viewport's, so box titles never clip. */}
            <div className="flex flex-wrap gap-4 *:flex-1 *:basis-72">
                {boxes(SUMMARY_SECTION_IDS)}
                <VersionCard label="Recommended patient investment">
                    <span className="text-base font-semibold text-gray-800 tabular-nums">
                        {formatMoneyCents(payload.fees.recommendedPatientInvestmentCents)}
                    </span>
                </VersionCard>
            </div>
            {boxes(DETAIL_SECTION_IDS)}
            {emptyLabels.length > 0 && (
                <p className="text-[11px] text-gray-400">Empty: {emptyLabels.join(' · ')}</p>
            )}
        </div>
    );
}

// The same PDF the Dx wizard previews before Save draft.
function DxReportPdf({ payload, onClose }: { payload: DxReportPayload; onClose: () => void }) {
    const state = useDxReportPdf(payload);
    return (
        <div className="flex-1 min-h-0 flex flex-col">
            <div className="px-5 pt-3 flex-shrink-0">
                <button type="button" onClick={onClose} className={LINK_BUTTON_CLASS}>
                    <ArrowLeftIcon className="w-3.5 h-3.5" />
                    Back to sections
                </button>
            </div>
            <DxReportPdfPane state={state} />
        </div>
    );
}

interface DxReportBodyProps {
    draft: TimelineEvent | null;
    payload: DxReportPayload | null;
    loading: boolean;
}

function DxReportBody({ draft, payload, loading }: DxReportBodyProps) {
    if (loading) return <p className={MUTED_TEXT_CLASS}>Loading the Dx report…</p>;
    if (!draft || !payload) {
        return <p className={MUTED_TEXT_CLASS}>No Dx report is waiting for review.</p>;
    }
    return <DxSections payload={payload} />;
}

function draftMeta(draft: TimelineEvent) {
    const author = draft.updatedByUser?.name ?? draft.createdByUser?.name ?? draft.createdBy;
    return (
        <>
            draft · {author} ·{' '}
            <span title={formatAbsolute(draft.updatedAt)}>
                {formatRelative(draft.updatedAt) || formatAbsolute(draft.updatedAt)}
            </span>
        </>
    );
}

interface FinalReviewDxReportProps {
    patientId: string;
    draft: TimelineEvent | null;
    draftsLoading: boolean;
    addNote: ReactNode;
}

export function FinalReviewDxReport({
    patientId,
    draft,
    draftsLoading,
    addNote
}: FinalReviewDxReportProps) {
    const { payload, isResolving } = useResolvedDxPayload(patientId, draft);
    const [showPdf, setShowPdf] = useState(false);

    return (
        <div className="flex-1 min-h-0 flex flex-col font-inter">
            {/* Drafts all sit in the version-0 slot, so there is no version number to show. */}
            <PanelHeader title="Dx report" meta={draft && draftMeta(draft)}>
                <button
                    type="button"
                    onClick={() => setShowPdf(true)}
                    disabled={!payload || showPdf}
                    className={HEADER_BUTTON_CLASS}
                >
                    <DocumentTextIcon className="w-3 h-3" />
                    PDF preview
                </button>
                {addNote}
            </PanelHeader>
            {showPdf && payload ? (
                <DxReportPdf payload={payload} onClose={() => setShowPdf(false)} />
            ) : (
                <div className={PANEL_BODY_CLASS}>
                    <DxReportBody
                        draft={draft}
                        payload={payload}
                        loading={draftsLoading || (isResolving && payload === null)}
                    />
                </div>
            )}
        </div>
    );
}
